import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  MdAlarm,
  MdAssignmentInd,
  MdDoneAll,
  MdErrorOutline,
  MdEventRepeat,
  MdInventory2,
  MdLocalShipping,
  MdNotificationsActive,
  MdNotificationsNone,
  MdReceiptLong,
  MdToday,
} from "react-icons/md";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import jobsServiceGlobal from "services/jobsService/jobs.service";
import notificacionesServiceGlobal from "services/notificacionesService/notificaciones.service";

// Cada cuánto se pregunta si hay avisos nuevos. Es solo un COUNT: barato.
const INTERVALO_AVISOS_MS = 60 * 1000;
// Los procesos en segundo plano terminan en segundos; se siguen mirando como antes.
const INTERVALO_PROCESOS_MS = 5 * 1000;

const VISUAL_TIPO = {
  FACTURA_CREADA: { icono: MdReceiptLong, color: "green" },
  REMISION_CREADA: { icono: MdLocalShipping, color: "blue" },
  REMISION_CERRADA: { icono: MdInventory2, color: "teal" },
  ACTIVIDAD_ASIGNADA: { icono: MdAssignmentInd, color: "purple" },
  ACTIVIDAD_PROXIMA: { icono: MdAlarm, color: "orange" },
  RESUMEN_DIARIO: { icono: MdToday, color: "cyan" },
  FACTURA_PROGRAMADA: { icono: MdEventRepeat, color: "green" },
  FACTURA_PROGRAMADA_ERROR: { icono: MdErrorOutline, color: "red" },
};

/** "hace 5 min", "hace 2 h", "ayer", "12/10". La fecha llega en hora local, sin zona. */
export const haceCuanto = (fecha, ahora = new Date()) => {
  const momento = new Date(fecha);
  if (Number.isNaN(momento.getTime())) return "";
  const minutos = Math.floor((ahora.getTime() - momento.getTime()) / 60000);
  if (minutos < 1) return "ahora";
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  if (horas < 48) return "ayer";
  return `${String(momento.getDate()).padStart(2, "0")}/${String(momento.getMonth() + 1).padStart(2, "0")}`;
};

// ---------------------------------------------------------------------------
// Procesos en segundo plano (cargues, descargas, correos). Es la lógica que
// vivía en NavbarLinksAdmin, sin cambios: se recuerdan en sessionStorage los
// que ya se avisaron para no repetirlos al recargar.
// ---------------------------------------------------------------------------

const ETIQUETAS_MODULO = {
  descarga_cliente: "Descarga de clientes",
  descarga_contacto: "Descarga de contactos",
  descarga_proveedor: "Descarga de proveedores",
  reportes_pdf: "Reportes",
  facturas_programadas: "Facturas programadas",
};

const formatModuleName = (value) => {
  const normalized = (value || "").toString().trim().toLowerCase();
  if (!normalized) return "N/A";
  if (ETIQUETAS_MODULO[normalized]) return ETIQUETAS_MODULO[normalized];
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "-" : parsed.toLocaleString("es-CO");
};

const buildJobMessage = (job) => {
  const moduleName = (job?.moduleName || "job").toString().trim().toLowerCase();
  const moduleLabel = formatModuleName(moduleName);
  const inserted = Number(job?.inserted || 0);
  const updated = Number(job?.updated || 0);
  const failed = Number(job?.failed || 0);

  // Un cargue puede traer altas y actualizaciones mezcladas; decir solo
  // "insertados" deja fuera la mitad del trabajo.
  const detalleCargue = updated > 0
    ? `Nuevos: ${inserted}, Actualizados: ${updated}`
    : `Insertados: ${inserted}`;

  if (moduleName === "correo") {
    if (job?.status === "completed") {
      return { status: "success", title: "Correo enviado", description: job?.message || "El correo fue enviado correctamente." };
    }
    if (job?.status === "completed_with_errors") {
      return {
        status: "warning",
        title: "Correo enviado con observaciones",
        description: job?.message || `Enviados: ${inserted}, Fallidos: ${failed}.`,
      };
    }
    return { status: "error", title: "Error al enviar correo", description: job?.message || "No fue posible completar el envío de correo." };
  }

  // El job de medianoche genera facturas, no carga filas de un Excel.
  if (moduleName === "facturas_programadas") {
    const generadas = `${inserted} ${inserted === 1 ? "factura generada" : "facturas generadas"}`;
    if (job?.status === "completed") {
      return { status: "success", title: "Facturas programadas", description: `${generadas}.` };
    }
    if (job?.status === "completed_with_errors") {
      return {
        status: "warning",
        title: "Facturas programadas con errores",
        description: `${generadas}, ${failed} sin generar. Se reintentan cada hora.`,
      };
    }
    return { status: "error", title: "Facturas programadas fallidas", description: job?.message || "No fue posible generarlas." };
  }

  // Una descarga no inserta nada: hablar de registros insertados ahi confunde.
  if (moduleName.startsWith("descarga_")) {
    if (job?.status === "completed") {
      return {
        status: "success",
        title: `${moduleLabel} lista`,
        description: `${inserted} registros. El archivo se descarga solo en la pestaña donde lo pediste.`,
      };
    }
    return { status: "error", title: `${moduleLabel} fallida`, description: job?.message || "No fue posible generar el archivo." };
  }

  if (job?.status === "completed") {
    return { status: "success", title: `Cargue finalizado (${moduleLabel})`, description: `${detalleCargue}.` };
  }
  if (job?.status === "completed_with_errors") {
    return { status: "warning", title: `Cargue finalizado con errores (${moduleLabel})`, description: `${detalleCargue}, Fallidos: ${failed}.` };
  }
  return { status: "error", title: `Cargue fallido (${moduleLabel})`, description: job?.message || "El job terminó con error." };
};

const leerAvisados = () => {
  try {
    const crudo = sessionStorage.getItem("jobs_notified_ids");
    return new Set(crudo ? JSON.parse(crudo) : []);
  } catch {
    return new Set();
  }
};

function Proceso({ notificacion }) {
  const [abierto, setAbierto] = useState(false);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const mutedTextColor = useColorModeValue("gray.600", "gray.300");
  const errorBoxBg = useColorModeValue("red.50", "red.900");
  const errorBoxBorder = useColorModeValue("red.200", "red.500");
  const errorItemBg = useColorModeValue("white", "whiteAlpha.200");

  return (
    <Alert status={notificacion.status} borderRadius="12px" mb="10px" alignItems="flex-start">
      <AlertIcon />
      <Flex direction="column" minW={0}>
        <AlertTitle fontSize="sm">{notificacion.title}</AlertTitle>
        <AlertDescription fontSize="xs">{notificacion.description}</AlertDescription>
        <Text fontSize="xs" mt="2px" color={textColor}>
          Job: {notificacion.jobShortId || "N/A"} | Módulo: {notificacion.moduleName || "N/A"}
        </Text>
        <Text fontSize="xs" color={textColor}>
          Inicio: {formatDateTime(notificacion.startedAt)} | Fin: {formatDateTime(notificacion.finishedAt)}
        </Text>
        {notificacion.failedRows.length > 0 && (
          <>
            <Button size="xs" mt="8px" alignSelf="flex-start" onClick={() => setAbierto((previo) => !previo)}>
              {abierto ? `Ocultar fallos (${notificacion.failedRows.length})` : `Ver fallos (${notificacion.failedRows.length})`}
            </Button>
            {abierto && (
              <Box mt="8px" maxH="180px" overflowY="auto" p="8px" borderWidth="1px" borderColor={errorBoxBorder} borderRadius="10px" bg={errorBoxBg}>
                <Text fontSize="xs" fontWeight="700" mb="6px" color={textColor}>Registros con error</Text>
                {notificacion.failedRows.map((fallo, indice) => (
                  <Box key={`${notificacion.jobId}-${indice}`} mb="6px" p="6px" borderRadius="8px" bg={errorItemBg}>
                    <Text fontSize="xs" fontWeight="700" color={textColor}>
                      {/* Los fallos de un job que no lee Excel traen su propia etiqueta. */}
                      {fallo?.etiqueta || `Fila Excel #${fallo?.rowNumber || "N/A"}`}
                    </Text>
                    <Text fontSize="xs" color={mutedTextColor}>{fallo?.error || "Error no especificado"}</Text>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </Flex>
    </Alert>
  );
}

function Aviso({ aviso, onAbrir }) {
  const textoPrincipal = useColorModeValue("secondaryGray.900", "white");
  const textoSecundario = useColorModeValue("gray.600", "gray.300");
  const fondoNoLeida = useColorModeValue("blue.50", "whiteAlpha.100");
  const fondoHover = useColorModeValue("gray.100", "whiteAlpha.200");
  const visual = VISUAL_TIPO[aviso.tipo] || { icono: MdNotificationsNone, color: "gray" };
  const fondoIcono = useColorModeValue(`${visual.color}.100`, "whiteAlpha.200");
  const colorIcono = useColorModeValue(`${visual.color}.600`, `${visual.color}.200`);

  return (
    <Flex
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      gap="12px"
      p="10px"
      borderRadius="12px"
      bg={aviso.leida ? "transparent" : fondoNoLeida}
      _hover={{ bg: fondoHover }}
      transition="background 0.15s ease"
      onClick={() => onAbrir(aviso)}
      aria-label={aviso.titulo}
    >
      <Flex w="36px" h="36px" flexShrink={0} borderRadius="full" align="center" justify="center" bg={fondoIcono} color={colorIcono}>
        <Icon as={visual.icono} boxSize="18px" />
      </Flex>
      <Box flex="1" minW={0}>
        <Text fontSize="sm" fontWeight={aviso.leida ? "500" : "700"} color={textoPrincipal} noOfLines={2}>
          {aviso.titulo}
        </Text>
        {aviso.mensaje && (
          <Text fontSize="xs" color={textoSecundario} noOfLines={2} mt="2px">{aviso.mensaje}</Text>
        )}
        <Text fontSize="11px" color={textoSecundario} mt="4px">{haceCuanto(aviso.fecha)}</Text>
      </Box>
      {!aviso.leida && <Box w="8px" h="8px" mt="6px" borderRadius="full" bg="brand.500" flexShrink={0} aria-label="No leída" />}
    </Flex>
  );
}

export default function CampanaNotificaciones() {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const navbarIcon = useColorModeValue("gray.500", "white");
  const fondoHover = useColorModeValue("gray.100", "whiteAlpha.200");
  const menuBg = useColorModeValue("white", "navy.800");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textoSecundario = useColorModeValue("gray.600", "gray.300");
  const shadow = useColorModeValue("14px 17px 40px 4px rgba(112, 144, 176, 0.18)", "14px 17px 40px 4px rgba(112, 144, 176, 0.06)");

  const [avisos, setAvisos] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [procesos, setProcesos] = useState([]);
  // null hasta la primera consulta: el primer conteo no debe sonar como nuevo.
  const ultimoConteo = useRef(null);

  const cargarAvisos = useCallback(async () => {
    setCargando(true);
    const respuesta = await notificacionesServiceGlobal.getNotificaciones({ limit: 30 });
    setCargando(false);
    if (respuesta?.error) return;
    setAvisos(Array.isArray(respuesta?.data) ? respuesta.data : []);
    const total = Number(respuesta?.noLeidas) || 0;
    setNoLeidas(total);
    ultimoConteo.current = total;
  }, []);

  const revisarConteo = useCallback(async () => {
    const respuesta = await notificacionesServiceGlobal.contarNotificaciones();
    if (respuesta?.error) return;
    const total = Number(respuesta?.noLeidas) || 0;
    const anterior = ultimoConteo.current;
    ultimoConteo.current = total;
    setNoLeidas(total);

    // Llegó algo mientras el usuario trabajaba: se le muestra el más reciente.
    if (anterior !== null && total > anterior) {
      const ultimas = await notificacionesServiceGlobal.getNotificaciones({ soloNoLeidas: true, limit: 1 });
      const nueva = Array.isArray(ultimas?.data) ? ultimas.data[0] : null;
      if (nueva) {
        toast({
          status: "info",
          title: nueva.titulo,
          description: nueva.mensaje || undefined,
          duration: 6000,
          isClosable: true,
          position: "top-right",
        });
      }
    }
  }, [toast]);

  const cargarProcesos = useCallback(async () => {
    const response = await jobsServiceGlobal.getMassiveJobs({ limit: 200 });
    const jobs = Array.isArray(response?.data) ? response.data : [];
    const terminados = jobs.filter((job) => ["completed", "completed_with_errors", "failed"].includes(job?.status));

    const avisados = leerAvisados();
    const siguientes = new Set(avisados);
    const nuevos = [];

    terminados.forEach((job) => {
      if (!job?.jobId || avisados.has(job.jobId)) return;
      const failedRows = (Array.isArray(job?.failedRows) ? job.failedRows : [])
        .slice()
        .sort((left, right) => {
          const leftRow = Number(left?.rowNumber);
          const rightRow = Number(right?.rowNumber);
          if (!Number.isFinite(leftRow) && !Number.isFinite(rightRow)) return 0;
          if (!Number.isFinite(leftRow)) return 1;
          if (!Number.isFinite(rightRow)) return -1;
          return leftRow - rightRow;
        });

      nuevos.push({
        jobId: job.jobId,
        jobShortId: (job?.jobId || "N/A").toString().slice(0, 8),
        moduleName: formatModuleName(job?.moduleName || "N/A"),
        startedAt: job?.startedAt || job?.createdAt || null,
        finishedAt: job?.finishedAt || job?.startedAt || job?.createdAt || null,
        failedRows,
        createdAt: job.finishedAt || job.createdAt,
        ...buildJobMessage(job),
      });
      siguientes.add(job.jobId);
    });

    if (nuevos.length > 0) {
      try {
        sessionStorage.setItem("jobs_notified_ids", JSON.stringify(Array.from(siguientes)));
      } catch {
        // Sin almacenamiento se pueden repetir al recargar; no es grave.
      }
      setProcesos((previo) => [...nuevos, ...previo].slice(0, 15));
    }
  }, []);

  useEffect(() => {
    cargarAvisos();
    const avisosId = setInterval(revisarConteo, INTERVALO_AVISOS_MS);
    cargarProcesos();
    const procesosId = setInterval(cargarProcesos, INTERVALO_PROCESOS_MS);
    return () => {
      clearInterval(avisosId);
      clearInterval(procesosId);
    };
  }, [cargarAvisos, revisarConteo, cargarProcesos]);

  const abrirPanel = () => {
    onOpen();
    cargarAvisos();
  };

  const abrirAviso = async (aviso) => {
    if (!aviso.leida) {
      setAvisos((previo) => previo.map((item) => (item.id === aviso.id ? { ...item, leida: true } : item)));
      setNoLeidas((previo) => Math.max(0, previo - 1));
      ultimoConteo.current = Math.max(0, (ultimoConteo.current || 1) - 1);
      notificacionesServiceGlobal.marcarLeida({ id: aviso.id });
    }
    if (aviso.enlace) {
      onClose();
      navigate(aviso.enlace);
    }
  };

  const marcarTodas = async () => {
    setAvisos((previo) => previo.map((item) => ({ ...item, leida: true })));
    setNoLeidas(0);
    ultimoConteo.current = 0;
    await notificacionesServiceGlobal.marcarTodasLeidas();
  };

  const total = noLeidas + procesos.length;

  return (
    <Popover isOpen={isOpen} onOpen={abrirPanel} onClose={onClose} placement="bottom-end" isLazy>
      <PopoverTrigger>
        <Box position="relative">
          <Tooltip label="Notificaciones" hasArrow openDelay={400}>
            <IconButton
              aria-label={total ? `Notificaciones, ${total} sin leer` : "Notificaciones"}
              icon={<Icon as={total ? MdNotificationsActive : MdNotificationsNone} boxSize={{ base: "22px", md: "24px" }} />}
              variant="ghost"
              borderRadius="full"
              w={{ base: "38px", md: "42px" }}
              h={{ base: "38px", md: "42px" }}
              minW="auto"
              color={navbarIcon}
              _hover={{ bg: fondoHover }}
            />
          </Tooltip>
          {total > 0 && (
            <Flex
              position="absolute"
              top="2px"
              right="0px"
              minW="18px"
              h="18px"
              px="4px"
              borderRadius="999px"
              bg="red.500"
              color="white"
              fontSize="10px"
              fontWeight="700"
              align="center"
              justify="center"
              borderWidth="2px"
              borderColor={menuBg}
              pointerEvents="none"
              data-testid="contador-notificaciones"
            >
              {total > 99 ? "99+" : total}
            </Flex>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent
        bg={menuBg}
        border="none"
        boxShadow={shadow}
        borderRadius="20px"
        w={{ base: "calc(100vw - 24px)", md: "400px" }}
        maxW="calc(100vw - 24px)"
      >
        <PopoverBody p={{ base: "14px", md: "18px" }}>
          <Flex justify="space-between" align="center" mb="8px" gap="8px">
            <Flex align="center" gap="8px">
              <Text fontSize="md" fontWeight="700" color={textColor}>Notificaciones</Text>
              {noLeidas > 0 && <Badge colorScheme="red" borderRadius="full">{noLeidas}</Badge>}
            </Flex>
            {noLeidas > 0 && (
              <Button size="xs" variant="ghost" leftIcon={<MdDoneAll />} onClick={marcarTodas}>
                Marcar todas como leídas
              </Button>
            )}
          </Flex>

          <Tabs size="sm" variant="soft-rounded" colorScheme="brand">
            {procesos.length > 0 && (
              <TabList mb="8px">
                <Tab>Avisos</Tab>
                <Tab>
                  Procesos
                  <Badge ml="6px" borderRadius="full">{procesos.length}</Badge>
                </Tab>
              </TabList>
            )}
            <TabPanels>
              <TabPanel p="0">
                <Box maxH="60vh" overflowY="auto" mx="-4px" px="4px">
                  {cargando && avisos.length === 0 ? (
                    <Flex direction="column" gap="8px">
                      <Skeleton h="56px" borderRadius="12px" />
                      <Skeleton h="56px" borderRadius="12px" />
                    </Flex>
                  ) : avisos.length === 0 ? (
                    <Flex direction="column" align="center" py="28px" color={textoSecundario} gap="6px">
                      <Icon as={MdNotificationsNone} boxSize="36px" />
                      <Text fontSize="sm" fontWeight="600">Estás al día</Text>
                      <Text fontSize="xs">Aquí verás facturas, remisiones y recordatorios de tus actividades.</Text>
                    </Flex>
                  ) : (
                    <Flex direction="column" gap="4px">
                      {avisos.map((aviso) => (
                        <Aviso key={aviso.id} aviso={aviso} onAbrir={abrirAviso} />
                      ))}
                    </Flex>
                  )}
                </Box>
              </TabPanel>
              <TabPanel p="0">
                <Box maxH="60vh" overflowY="auto">
                  {procesos.map((notificacion) => (
                    <Proceso key={notificacion.jobId} notificacion={notificacion} />
                  ))}
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
